<template>
  <div
    class="absolute absolute--fill flex flex-column"
    ref="bottomBar"
  >
    <div
      class="relative"
      style="flex-grow: 1;"
      v-touch:swipe.up="navigate"
    >
      <SlideUpButton
        ref="slideUpButton"
        :brand="development.brand"
      />
      <div
        v-if="houseStyle.featured_image"
        class="absolute absolute--fill cover bg-center kenburns-bottom"
        :style="'background-image: url(' + houseStyle.featured_image + ')'"
      />
      <div
        class="absolute absolute--fill"
        style="background: linear-gradient(90deg, rgba(0, 0, 0, 0.25) 0%, rgba(0, 0, 0, 0) 49.45%)"
      />
    </div>

    <div class="bg-white shadow-5 ba b--black-10 flex items-center z-999">
      <div class="w-60 flex items-center pv3">
        <div class="ph5">
          <Button
            :brand="development.brand"
            :icon="arrowLeft"
            text="Back to siteplan"
            @click="$router.push('/developments/' + $route.params.development)"
          />
        </div>
        <div class="relative overflow-hidden">
          <div
            ref="title"
            v-if="plot.houseType"
            class="title f1 b tc ph3"
            :class="{
              'active': loaded,
              'purple': development.brand === 'lh',
              'dark-gray': development.brand === 'lq'
            }"
          >
            {{ plot.houseType }}
          </div>
        </div>
        <div class="name-bar-container">
          <div
            class="name-bar bl mv3"
            :class="{
              'active': loaded,
              'purple b--purple': development.brand === 'lh',
              'dark-gray b--dark-gray': development.brand === 'lq'
            }"
          />
        </div>
        <div class="relative overflow-hidden">
          <div
            ref="plot"
            v-if="plot.name"
            class="plot f2 flex items-center ph3 pv3 tc"
            :class="{
              'active': loaded,
              'purple': development.brand === 'lh',
              'dark-gray': development.brand === 'lq'
            }"
          >
            <div class="db">
              {{ plot.name }}
            </div>
          </div>
        </div>
      </div>
      <div
        class="flex items-center align-center flex-wrap w-40 h-100"
        :class="{
          'bg-light-gray': development.brand === 'lh',
          'bg-dark-gray white': development.brand === 'lq'
        }"
      >
        <div class="flex flex-wrap items-center align-center w-100">
          <div class="flex items-center align-center ph4">
            <div
              v-if="plot.price && plot.sellingType === 'Shared Ownership' && plot.statusCode === 'Available'"
              class="f2 pr4 b lh-copy"
              :class="{
                'purple-deep': development.brand === 'lh',
                'dark-gray': development.brand === 'lq'
              }"
            >
              {{ parseInt(plot.price) / 4 | formatPrice }}
            </div>
            <div
              v-else-if="plot.price && plot.statusCode === 'Available'"
              class="f2 pr4 b lh-copy"
              :class="{
                'purple-deep': development.brand === 'lh',
                'dark-gray': development.brand === 'lq'
              }"
            >
              {{ plot.price | formatPrice }}
            </div>
            <div
              v-else-if="plot.price && plot.statusCode !== 'Available'"
              class="f2 pr4 b lh-copy"
              :class="{
                'purple-deep': development.brand === 'lh',
                'dark-gray white': development.brand === 'lq'
              }"
            >
              {{ plot.statusCode }}
            </div>
            <div
              v-else
              class="f2 pr4 b lh-copy"
              :class="{
                'purple-deep': development.brand === 'lh',
                'dark-gray white': development.brand === 'lq'
              }"
            >
              &pound; TBC
            </div>
            <div class="pv4 tc flex justify-around align-center">
              <Pill
                v-if="plot.bedroomsCount"
                :class="{
                  'bg-white purple-deep': development.brand === 'lh',
                  'dark-gray white': development.brand === 'lq'
                }"
                :info="plot.bedroomsCount + ' beds'"
              />
              <Pill
                v-if="plot.unitType"
                :class="{
                  'bg-white purple-deep': development.brand === 'lh',
                  'dark-gray white': development.brand === 'lq'
                }"
                :info="plot.unitType"
              />
              <Pill
                v-if="plot.sellingType === 'Shared Ownership'"
                :class="{
                  'bg-white purple-deep': development.brand === 'lh',
                  'dark-gray white': development.brand === 'lq'
                }"
                :info="plot.sellingType"
              />
              <Pill
                :class="{
                  'bg-white purple-deep': development.brand === 'lh',
                  'dark-gray white': development.brand === 'lq'
                }"
                :info="Intl.NumberFormat('en-US').format(houseStyle.size_sqft) + ' sqft'"
              />
            </div>
          </div>
          <!-- f2 pr4 purple-deep b lh-copy -->
          <div
            class="w-100 pv3 ph4 flex flex-wrap items-center"
            :class="{
              'bg-purple white': development.brand === 'lh',
              'bg-yellow dark-gray': development.brand === 'lq'
            }"
          >
            <div class="f3 pr4 lh-copy">
              Sales Advisor
            </div>
            <div
              class="f3 pr1 lh-copy fw6"
              v-if="salesAdvisor.name"
            >
              {{ salesAdvisor.name }} -
            </div>
            <div
              class="f3 pr4 lh-copy fw6"
              v-if="salesAdvisor.tel"
            >
              0{{ salesAdvisor.tel }}
            </div>
          </div>
        </div>
        <div
          v-if="plot.price && plot.sellingType === 'Shared Ownership'"
          class="w-100 f7 lh-copy purple-deep"
          :class="{
            'purple-deep': development.brand === 'lh',
            'dark-gray': development.brand === 'lq'
          }"
        >
          Represents a 25% share value of the full market value of {{ plot.price | formatPrice }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from '@/components/Button'
import Pill from '@/components/Pill'
import SlideUpButton from '@/components/property/SlideUpButton'
// import gsap from 'gsap'

export default {
  name: 'Intro',
  components: {
    Button,
    Pill,
    SlideUpButton
  },
  props: {
    development: {
      type: Object,
      default: null
    },
    plot: {
      type: Object,
      default: null
    },
    houseStyle: {
      type: Object,
      default: null
    },
    salesAdvisor: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      arrowLeft: require('@/assets/img/arrow-left-circle.svg'),
      loaded: false
    }
  },
  methods: {
    navigate () {
      this.$store.commit('setSidebarNav', 0)
      this.$store.commit('setActiveTab', 0)
      this.$router.push(this.$route.path + '/floorplans')
    }
  },
  mounted () {
    if (this.$refs.slideUpButton) {
      this.$refs.slideUpButton.animateButton()
    }
    this.loaded = true
  }
}
</script>

<style lang="scss" scoped>
  .name-bar-container {
    height: 6rem;
  }
  .name-bar {
    height: 0rem;
    transition: height 0.5s ease;
    transition-delay: 0.5s;
    &.active {
      height: 4rem;
    }
  }

  .title {
    position: relative;
    transform: translateX(106%);
    transition: transform 0.5s ease;
    transition-delay: 1s;

    &.active {
      transform: translateX(0%);
    }
  }

  .plot {
    position: relative;
    transform: translateX(-106%);
    transition: transform 0.5s ease;
    transition-delay: 1s;

    &.active {
      transform: translateX(0%);
    }
  }

  .kenburns-bottom {
    animation: kenburns-bottom 4s ease-out both;
  }
  @keyframes kenburns-bottom {
  0% {
    transform: scale(1) translateY(0);
    transform-origin: 50% 84%;
  }
  100% {
    transform: scale(1.15) translateY(15px);
    transform-origin: bottom;
  }
}

</style>
